import {
  ButtonGroup,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  CalendarPlus,
  CaretDown,
  Plus,
  UsersThree,
  VideoCamera,
} from '@phosphor-icons/react';
import { useId } from '@react-aria/utils';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { z } from 'zod';

import { InPersonMeetingForm } from './MeetingForms';

import { axiosInstance } from '../../../../../assets/utils/axiosInstance';
import { useToastNotificationContext } from '../../../../../hooks/global/alert/useToastNotification';
import { CustomError } from '../../../../../interfaces/ErrorType';
import {
  MeetingDetailItemObject,
  setError,
  setIsMeetingSent,
  setItem,
  setLoading,
} from '../../../../../slices/instantMeetingSlice';
import { setUserId } from '../../../../../slices/meetings';
import { RootState } from '../../../../../utils/store';

import InstantMeetingModal from '~/components/customer/report/reportActions/otherActions/instantMeetingModal';
import ScheduleMeetingModal from '~/components/customer/report/reportActions/otherActions/scheduleMeetingModal';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '~/components/ui/dialog';
import { ROLE_VALUES } from '~/constants/customerConstant';
import { useTenantSlug } from '~/features/tenant/helpers';
import QueryResolver from '~/services/api/query-resolver';
import { ReportService } from '~/services/api/reports/endpoints';
import { useWrappedMutation } from '~/services/api/rest-api';
import { StatusCodeErrorBanner } from '~/services/api/rest-api-error';
import { ScheduleService } from '~/services/api/schedules/endpoints';
import { useAuthSlice } from '~/slices/auth';
import { toPayloadDateFormat, toPayloadTimeFormat } from '~/utils/helpers';

interface SetupMeetingProps {
  reportId: string;
  assignee: string;
  conversation: string;
}

interface InstantMeetingFromAPI {
  id: number;
  created_by: {
    id: number;
    full_name: string;
  };
  link: string;
  date_and_time: string;
  type: number;
  report: {
    id: string;
    report_type: number;
    country_verbose: {
      code: string;
      name: string;
    };
  };
}

const transformData = (
  data: InstantMeetingFromAPI,
  reportId: string,
): MeetingDetailItemObject => {
  const transformedData: MeetingDetailItemObject = {
    id: data.id,
    link: data.link,
    type: data.type,
    name: '',
    organizer: data.created_by?.full_name ?? '',
    country: data.report?.country_verbose?.name ?? '',
    reportType: data.report?.report_type,
    date: data.date_and_time,
    hasInstantMeetings: true,
    reportId,
  };

  return transformedData;
};

export default function SetupMeeting({
  reportId,
  assignee,
  conversation,
}: SetupMeetingProps) {
  const intl = useIntl();
  const userType = useAuthSlice().auth.user.type;
  const isAdmin =
    userType === ROLE_VALUES.PARTNER_ADMIN ||
    userType === ROLE_VALUES.TENANT_ADMIN;
  const { createToastNotification } = useToastNotificationContext();
  const [meetingType, setMeetingType] = useState<
    'instant' | 'scheduled' | 'in-person' | undefined
  >();
  const [openInstantMeetingModal, setOpenInstantMeetingModal] =
    useState<boolean>(false);
  const [openScheduleMeetingModal, setOpenScheduleMeetingModal] =
    useState<boolean>(false);

  const dispatch = useDispatch();
  const meeting = useSelector((state: RootState) => state.instantMeeting.item);
  const isCreatingInstantMeeting = useSelector(
    (state: RootState) => state.instantMeeting.loading,
  );
  const userId = useSelector((state: RootState) => state.meetings.userId);

  const { data } = useSWR('user/profile');
  dispatch(setUserId(data?.id));

  const createInstantMeeting = async () => {
    if (!meeting.hasInstantMeetings && meeting.reportId !== reportId) {
      dispatch(setLoading(true));
      dispatch(setError(false));
      try {
        const response = await axiosInstance().post('schedule/meetings', {
          date_and_time: new Date(Date.now()).toISOString(),
          type: 2,
          report: reportId,
          is_real_time_meeting: true,
        });
        if (response) {
          dispatch(setItem(transformData(response?.data, reportId)));
          createToastNotification(
            'Instant Meeting Successfully Created.',
            'success',
          );
        }
        dispatch(setLoading(false));
        dispatch(setIsMeetingSent(false));
      } catch (err) {
        const customError = err as CustomError;
        const errorMessage =
          customError.message ||
          intl.formatMessage({
            defaultMessage: 'Something went wrong, please try again.',
          });
        createToastNotification(errorMessage, 'error');
        dispatch(setLoading(false));
        dispatch(setError(true));
      }
    }
  };

  // INSTANT MEETING
  const assigneeId = z.coerce.number().catch(0).parse(assignee);
  const isAllowedToCreateMeeting = assigneeId === userId || isAdmin;

  const handleOpenInstantMeeting = () => {
    setOpenInstantMeetingModal(true);
    createInstantMeeting();
  };

  const handleCloseInstantMeeting = () => {
    if (isCreatingInstantMeeting) {
      return;
    }

    setOpenInstantMeetingModal(false);
  };

  return (
    <div className='space-y-1 isolate'>
      <ButtonGroup
        disabled={!isAllowedToCreateMeeting}
        size='lg'
        variant='outlined'
        color='neutral'
        aria-label='split button'
        slotProps={{
          root: ({ disabled }) => ({
            sx: {
              backgroundColor: (theme) =>
                disabled ? theme.palette.neutral[50] : undefined,
            },
          }),
        }}
      >
        <Button
          startDecorator={<VideoCamera />}
          sx={{ flex: 1 }}
          onClick={() => handleOpenInstantMeeting()}
        >
          <FormattedMessage defaultMessage='Instant meeting' />
        </Button>
        <Dropdown>
          <MenuButton data-last-child slots={{ root: IconButton }}>
            <CaretDown />
          </MenuButton>
          <Menu placement='bottom-end'>
            <MenuItem onClick={() => handleOpenInstantMeeting()}>
              <Plus />
              <FormattedMessage defaultMessage='Instant Meeting' />
            </MenuItem>
            <MenuItem onClick={() => setOpenScheduleMeetingModal(true)}>
              <CalendarPlus />
              <FormattedMessage defaultMessage='Schedule a Meeting' />
            </MenuItem>
            <MenuItem onClick={() => setMeetingType('in-person')}>
              <UsersThree />
              <FormattedMessage defaultMessage='Schedule in-person meet' />
            </MenuItem>
          </Menu>
        </Dropdown>
      </ButtonGroup>
      {!isAllowedToCreateMeeting && (
        <Typography level='body-sm' fontWeight='md'>
          {intl.formatMessage({ defaultMessage: 'Action not allowed' })}.{' '}
          <Tooltip
            title={intl.formatMessage({
              defaultMessage:
                'Only assignee or admin can create meetings for this report.',
            })}
          >
            <Typography
              color='secondary'
              className='hover:underline cursor-pointer'
            >
              {intl.formatMessage({ defaultMessage: 'Learn More' })}
            </Typography>
          </Tooltip>
        </Typography>
      )}

      {/* Instant Meeting Modal */}
      <InstantMeetingModal
        open={openInstantMeetingModal}
        setClose={handleCloseInstantMeeting}
        conversation={conversation}
      />

      {/* Schedule Meeting Modal */}
      <ScheduleMeetingModal
        open={openScheduleMeetingModal}
        setClose={() => setOpenScheduleMeetingModal(false)}
        reportId={reportId}
      />

      <InPersonMeetingFormModal
        open={meetingType === 'in-person'}
        onClose={() => setMeetingType(undefined)}
      />
    </div>
  );
}

function InPersonMeetingFormModal({
  open: shouldOpen = false,
  onClose,
}: {
  open?: boolean;
  onClose?: () => void;
}) {
  const { createToastNotification } = useToastNotificationContext();
  const referenceId = useParams().reportId ?? '';
  const tenantSlug = useTenantSlug();
  const query = ReportService.endpoints.report.useQuery({
    referenceId,
    tenantSlug,
  });
  const reportId = query.data?.id ?? '';

  const [open, setOpen] = useState(shouldOpen);
  useEffect(() => {
    setOpen(shouldOpen);
  }, [shouldOpen]);

  const intl = useIntl();
  const formId = useId();
  const scheduleMeeting = useWrappedMutation(
    ScheduleService.endpoints.scheduleMeeting.useMutation(),
  );
  const isSubmitting = scheduleMeeting.state.isLoading;

  return (
    <Dialog open={open} onOpenChange={setOpen} isDismissable={!isSubmitting}>
      <DialogContent
        onCloseAutoFocus={() => onClose && onClose()}
        onInteractOutside={(e) => isSubmitting && e.preventDefault()}
      >
        <DialogTitle>
          {intl.formatMessage({ defaultMessage: 'Schedule in-person meet' })}
        </DialogTitle>
        <DialogBody>
          <StatusCodeErrorBanner error={scheduleMeeting.state.error} />
          <QueryResolver query={query}>
            <InPersonMeetingForm
              id={formId}
              defaultValues={{ reportId }}
              onSubmit={async (data) => {
                await scheduleMeeting.mutateAsync(
                  {
                    type: 'IN_PERSON',
                    ...data,
                    date_and_time: `${toPayloadDateFormat(
                      data.date,
                    )}T${toPayloadTimeFormat(data.time, {
                      includeSeconds: true,
                    })}`,
                    report: data.reportId,
                  },
                  {
                    onSuccess: () => {
                      createToastNotification(
                        intl.formatMessage({
                          defaultMessage: 'Meeting successfully scheduled',
                        }),
                        'success',
                      );
                      if (onClose) {
                        onClose();
                      }
                    },
                  },
                );
              }}
            />
          </QueryResolver>
        </DialogBody>
        <DialogActions>
          <DialogClose asChild>
            <Button color='neutral' variant='plain' disabled={isSubmitting}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            form={formId}
            type='submit'
            color='secondary'
            loading={isSubmitting}
          >
            {intl.formatMessage({ defaultMessage: 'Schedule Meeting' })}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

// function PreScheduledMeetingDialog({
//   open: shouldOpen = false,
//   onClose,
// }: {
//   open?: boolean;
//   onClose?: () => void;
// }) {
//   const referenceId = useParams().reportId ?? '';
//   const tenantSlug = useTenantSlug();
//   const query = ReportService.endpoints.report.useQuery({
//     referenceId,
//     tenantSlug,
//   });
//   const reportId = query.data?.id ?? '';

//   const [open, setOpen] = useState(shouldOpen);
//   useEffect(() => {
//     setOpen(shouldOpen);
//   }, [shouldOpen]);

//   const intl = useIntl();
//   const formId = useId();
//   const scheduleMeeting = useWrappedMutation(
//     ScheduleService.endpoints.scheduleMeeting.useMutation(),
//   );
//   const isSubmitting = scheduleMeeting.state.isLoading;

//   return (
//     <Dialog open={open} onOpenChange={setOpen} isDismissable={!isSubmitting}>
//       <DialogContent
//         onCloseAutoFocus={() => onClose && onClose()}
//         onInteractOutside={(e) => isSubmitting && e.preventDefault()}
//       >
//         <DialogTitle>
//           {intl.formatMessage({ defaultMessage: 'Schedule a meet' })}
//         </DialogTitle>
//         <DialogBody>
//           <StatusCodeErrorBanner error={scheduleMeeting.state.error} />
//           <QueryResolver query={query}>
//             <PreScheduledMeetingForm
//               id={formId}
//               defaultValues={{ reportId }}
//               onSubmit={async (data) => {
//                 await scheduleMeeting.mutateAsync(
//                   {
//                     type: 1,
//                     ...data,
//                     report: data.reportId,
//                     date_and_time: `${toPayloadDateFormat(
//                       data.date,
//                     )}T${toPayloadTimeFormat(data.time, {
//                       includeSeconds: true,
//                     })}`,
//                     callType: data.callType,
//                   },
//                   { onSuccess: () => onClose && onClose() },
//                 );
//               }}
//             />
//           </QueryResolver>
//         </DialogBody>
//         <DialogActions>
//           <DialogClose asChild>
//             <Button color='neutral' variant='plain' disabled={isSubmitting}>
//               Cancel
//             </Button>
//           </DialogClose>
//           <Button
//             form={formId}
//             type='submit'
//             color='secondary'
//             loading={isSubmitting}
//           >
//             {intl.formatMessage({ defaultMessage: 'Schedule Meeeting' })}
//           </Button>
//         </DialogActions>
//       </DialogContent>
//     </Dialog>
//   );
// }
